import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Loader2, Plus, MoreHorizontal, Trash2, Webhook, Copy, AlertTriangle,
  CheckCircle2, XCircle, PlayCircle, Pencil, History, AlertCircle,
} from 'lucide-react';

import { PageHeader } from '@/components/layout/page-header';
import { Pagination } from '@/components/data-table/pagination';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

import {
  useOutboundWebhooks, useWebhookEventTypes, useCreateWebhook, useUpdateWebhook,
  useDeleteWebhook, useTestWebhook, useWebhookDeliveries,
  type OutboundWebhook, type CreateWebhookResult,
} from '@/hooks/use-outbound-webhooks';
import { toast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api-client';
import { relativeTime, absoluteTime, cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/outbound-webhooks')({
  component: WebhooksPage,
});

function WebhooksPage() {
  const { data, isLoading } = useOutboundWebhooks();
  const [dialogMode, setDialogMode] = React.useState<{ kind: 'create' } | { kind: 'edit'; webhook: OutboundWebhook } | null>(null);
  const [createdWebhook, setCreatedWebhook] = React.useState<CreateWebhookResult | null>(null);
  const [viewingDeliveries, setViewingDeliveries] = React.useState<OutboundWebhook | null>(null);

  return (
    <>
      <PageHeader
        title="Webhooks"
        description="Forward WeRemote events to external systems (Slack, n8n, your own apps). Each delivery is signed with HMAC-SHA256."
        actions={
          <Button onClick={() => setDialogMode({ kind: 'create' })}>
            <Plus /><span>New webhook</span>
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Events</TableHead>
              <TableHead>Last 7 days</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </TableCell>
              </TableRow>
            ) : (data?.data ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                  <Webhook className="mx-auto h-5 w-5 mb-2 opacity-50" />
                  No webhooks yet. Create one to forward events to external systems.
                </TableCell>
              </TableRow>
            ) : (
              data?.data.map((w) => (
                <WebhookRow
                  key={w.id}
                  webhook={w}
                  onEdit={() => setDialogMode({ kind: 'edit', webhook: w })}
                  onViewDeliveries={() => setViewingDeliveries(w)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {dialogMode?.kind === 'create' && (
        <CreateOrEditDialog
          mode="create"
          onClose={() => setDialogMode(null)}
          onCreated={(result) => {
            setDialogMode(null);
            setCreatedWebhook(result);
          }}
        />
      )}

      {dialogMode?.kind === 'edit' && (
        <CreateOrEditDialog
          mode="edit"
          webhook={dialogMode.webhook}
          onClose={() => setDialogMode(null)}
        />
      )}

      <NewlyCreatedDialog
        webhook={createdWebhook}
        onClose={() => setCreatedWebhook(null)}
      />

      <DeliveriesDialog
        webhook={viewingDeliveries}
        onClose={() => setViewingDeliveries(null)}
      />
    </>
  );
}

function WebhookRow({ webhook, onEdit, onViewDeliveries }: {
  webhook: OutboundWebhook;
  onEdit: () => void;
  onViewDeliveries: () => void;
}) {
  const update = useUpdateWebhook();
  const deleteMut = useDeleteWebhook();
  const test = useTestWebhook();

  function toggleActive(): void {
    update.mutate(
      { id: webhook.id, input: { active: !webhook.active } },
      {
        onSuccess: () => toast({ title: webhook.active ? 'Webhook paused' : 'Webhook resumed' }),
        onError: (err) => toast({
          variant: 'destructive',
          title: 'Update failed',
          description: err instanceof ApiError ? err.message : 'Try again',
        }),
      },
    );
  }

  function handleDelete(): void {
    if (!window.confirm(`Delete webhook "${webhook.name}"? Delivery history will be lost.`)) return;
    deleteMut.mutate(webhook.id, {
      onSuccess: () => toast({ title: 'Webhook deleted' }),
      onError: (err) => toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: err instanceof ApiError ? err.message : 'Try again',
      }),
    });
  }

  function handleTest(): void {
    test.mutate(webhook.id, {
      onSuccess: () => toast({ title: 'Test event dispatched', description: 'Check the deliveries history in a few seconds.' }),
      onError: (err) => toast({
        variant: 'destructive',
        title: 'Test failed',
        description: err instanceof ApiError ? err.message : 'Try again',
      }),
    });
  }

  const stats = webhook.recentDeliveries;
  const total = (stats?.success ?? 0) + (stats?.failed ?? 0);
  const failureRate = total === 0 ? 0 : Math.round((stats!.failed / total) * 100);

  return (
    <TableRow className={cn(!webhook.active && 'opacity-60')}>
      <TableCell>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{webhook.name}</span>
          <span className="text-2xs text-muted-foreground">Created {relativeTime(webhook.createdAt)}</span>
        </div>
      </TableCell>
      <TableCell>
        <code className="font-mono text-xs text-muted-foreground truncate block max-w-xs" title={webhook.url}>
          {webhook.url}
        </code>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1 max-w-xs">
          {webhook.eventTypes.slice(0, 3).map((e) => (
            <Badge key={e} variant="outline" className="font-mono text-2xs">{e}</Badge>
          ))}
          {webhook.eventTypes.length > 3 && (
            <Badge variant="outline" className="font-mono text-2xs">+{webhook.eventTypes.length - 3}</Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        {!stats || total === 0 ? (
          <span className="text-2xs text-muted-foreground italic">No deliveries</span>
        ) : (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2 font-mono text-xs tabular-nums">
              <span className="text-success">{stats.success}</span>
              <span className="text-muted-foreground">/</span>
              <span className={cn(stats.failed > 0 ? 'text-destructive' : 'text-muted-foreground')}>
                {stats.failed}
              </span>
              {failureRate > 0 && (
                <Badge variant={failureRate > 20 ? 'destructive' : 'warning'} className="ml-1 text-2xs">
                  {failureRate}% fail
                </Badge>
              )}
            </div>
            {stats.lastDeliveryAt && (
              <span className="text-2xs text-muted-foreground">{relativeTime(stats.lastDeliveryAt)}</span>
            )}
          </div>
        )}
      </TableCell>
      <TableCell>
        {webhook.active ? (
          <Badge variant="success">Active</Badge>
        ) : (
          <Badge variant="secondary">Paused</Badge>
        )}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon"><MoreHorizontal /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onSelect={handleTest}>
              <PlayCircle /><span>Send test event</span>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onViewDeliveries}>
              <History /><span>View deliveries</span>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onEdit}>
              <Pencil /><span>Edit</span>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={toggleActive}>
              <span>{webhook.active ? 'Pause' : 'Resume'}</span>
            </DropdownMenuItem>
            <DropdownMenuItem destructive onSelect={handleDelete}>
              <Trash2 /><span>Delete</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

const webhookSchema = z.object({
  name: z.string().min(1, 'Required').max(100),
  url: z.string().url('Must be a valid URL'),
  eventTypes: z.array(z.string()).min(1, 'Select at least one event'),
});
type WebhookValues = z.infer<typeof webhookSchema>;

function CreateOrEditDialog({
  mode, webhook, onClose, onCreated,
}: {
  mode: 'create' | 'edit';
  webhook?: OutboundWebhook;
  onClose: () => void;
  onCreated?: (result: CreateWebhookResult) => void;
}) {
  const create = useCreateWebhook();
  const update = useUpdateWebhook();
  const eventTypes = useWebhookEventTypes();

  const form = useForm<WebhookValues>({
    resolver: zodResolver(webhookSchema),
    defaultValues: {
      name: webhook?.name ?? '',
      url: webhook?.url ?? '',
      eventTypes: webhook?.eventTypes ?? [],
    },
  });

  function toggleEvent(e: string): void {
    const current = form.getValues('eventTypes');
    form.setValue(
      'eventTypes',
      current.includes(e) ? current.filter((x) => x !== e) : [...current, e],
      { shouldValidate: true },
    );
  }

  function onSubmit(values: WebhookValues): void {
    if (mode === 'create') {
      create.mutate(values, {
        onSuccess: (result) => onCreated?.(result),
        onError: (err) => toast({
          variant: 'destructive',
          title: 'Could not create webhook',
          description: err instanceof ApiError ? err.message : 'Try again',
        }),
      });
    } else if (webhook) {
      update.mutate(
        { id: webhook.id, input: values },
        {
          onSuccess: () => {
            toast({ title: 'Webhook updated' });
            onClose();
          },
          onError: (err) => toast({
            variant: 'destructive',
            title: 'Update failed',
            description: err instanceof ApiError ? err.message : 'Try again',
          }),
        },
      );
    }
  }

  const selected = form.watch('eventTypes');
  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New outbound webhook' : 'Edit webhook'}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Configure a URL that will receive signed POST requests for selected events.'
              : 'Update the webhook configuration. The signing secret cannot be changed; delete and recreate if needed.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="w-name">Name</Label>
            <Input id="w-name" placeholder="Slack #ops, n8n flow…" autoFocus {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="w-url">URL</Label>
            <Input
              id="w-url"
              placeholder="https://hooks.example.com/incoming/abc123"
              type="url"
              {...form.register('url')}
            />
            {form.formState.errors.url && (
              <p className="text-xs text-destructive">{form.formState.errors.url.message}</p>
            )}
            <p className="text-2xs text-muted-foreground">
              Must be HTTPS or HTTP. Private network addresses (localhost, 192.168.x.x, etc.) are blocked for security.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Events to forward</Label>
            {form.formState.errors.eventTypes && (
              <p className="text-xs text-destructive">{form.formState.errors.eventTypes.message}</p>
            )}
            <div className="border rounded-md p-2 space-y-1 max-h-48 overflow-y-auto bg-muted/20">
              {eventTypes.isLoading ? (
                <Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                eventTypes.data?.data.map((e) => (
                  <label key={e} className="flex items-center gap-2 cursor-pointer hover:bg-accent/50 rounded px-2 py-1">
                    <input
                      type="checkbox"
                      checked={selected.includes(e)}
                      onChange={() => toggleEvent(e)}
                      className="h-3.5 w-3.5 rounded border-input"
                    />
                    <span className="font-mono text-xs">{e}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={pending}>
              {pending && <Loader2 className="animate-spin" />}
              {mode === 'create' ? 'Create webhook' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NewlyCreatedDialog({ webhook, onClose }: { webhook: CreateWebhookResult | null; onClose: () => void }) {
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => { if (webhook) setCopied(false); }, [webhook]);

  if (!webhook) return null;

  function copy(): void {
    navigator.clipboard.writeText(webhook!.secret).then(() => {
      setCopied(true);
      toast({ title: 'Secret copied to clipboard' });
    });
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Webhook created — copy the signing secret</DialogTitle>
          <DialogDescription>
            This is the only time the secret will be shown. Configure your receiver to verify the
            <span className="font-mono"> X-WR-Signature</span> header using this value.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-3">
            <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <p className="text-xs">
              The signature is computed as <span className="font-mono">HMAC-SHA256(secret, timestamp + "." + nonce + "." + body)</span>.
              See the documentation for full verification instructions.
            </p>
          </div>

          <div>
            <Label className="mb-2 block">Signing secret</Label>
            <div className="flex gap-2">
              <code className="flex-1 rounded-md border bg-muted/30 px-3 py-2 font-mono text-xs break-all">
                {webhook.secret}
              </code>
              <Button onClick={copy} variant={copied ? 'outline' : 'default'}>
                {copied ? <CheckCircle2 /> : <Copy />}
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeliveriesDialog({ webhook, onClose }: { webhook: OutboundWebhook | null; onClose: () => void }) {
  const [page, setPage] = React.useState(1);
  const { data, isLoading } = useWebhookDeliveries({
    webhookId: webhook?.id,
    page,
    pageSize: 25,
  });

  if (!webhook) return null;

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Delivery history — {webhook.name}</DialogTitle>
          <DialogDescription>
            Last 30 days of attempted deliveries. Older entries are purged automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Response</TableHead>
                <TableHead>Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-20 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                  </TableCell>
                </TableRow>
              ) : (data?.data ?? []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-20 text-center text-muted-foreground text-sm italic">
                    No deliveries yet. Send a test event to verify the connection.
                  </TableCell>
                </TableRow>
              ) : (
                data?.data.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-2xs">{d.eventType}</Badge>
                    </TableCell>
                    <TableCell>
                      {d.status === 'success' ? (
                        <span className="inline-flex items-center gap-1 text-success">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          <span className="text-xs">Success</span>
                        </span>
                      ) : d.status === 'failed' ? (
                        <span className="inline-flex items-center gap-1 text-destructive">
                          <XCircle className="h-3.5 w-3.5" />
                          <span className="text-xs">Failed</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-warning">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          <span className="text-xs">Pending</span>
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs tabular-nums">{d.attempts}</TableCell>
                    <TableCell>
                      {d.responseStatus !== null ? (
                        <span className="font-mono text-xs">
                          HTTP {d.responseStatus}
                        </span>
                      ) : d.errorMsg ? (
                        <span className="text-2xs text-destructive line-clamp-1" title={d.errorMsg}>
                          {d.errorMsg}
                        </span>
                      ) : (
                        <span className="text-2xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-2xs tabular-nums text-muted-foreground">
                      {absoluteTime(d.lastTriedAt ?? d.createdAt)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {data && data.total > data.pageSize && (
          <div className="border-t pt-3">
            <Pagination page={page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
